import { Calendar, Clock, ExternalLink, Mail, MessageSquare, Phone } from "lucide-react";
import { Lead } from "../../../crm-pages/hot-leads/types";

export const LeadCard: React.FC<{ lead: Lead; expanded: boolean; onToggle: () => void }> = ({ 
  lead, 
  expanded, 
  onToggle 
}) => {
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Hot': return 'bg-orange-100 text-orange-700';
      case 'Warm': return 'bg-yellow-100 text-yellow-700';
      case 'Cold': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'LinkedIn': return '💼';
      case 'Website': return '🌐';
      case 'Google Ads': return '🔍';
      case 'Referral': return '👥';
      case 'Cold Outreach': return '✉️';
      default: return '📌';
    }
  };

  const getActionIcon = (type: string) => {
    switch (type) {
      case 'email': return <Mail className="w-4 h-4" />;
      case 'call': return <Phone className="w-4 h-4" />;
      case 'message': return <MessageSquare className="w-4 h-4" />;
      case 'ai': return <span className="text-sm">✨</span>;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 hover:shadow-md transition-shadow">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-blue-500 text-white flex items-center justify-center font-semibold text-lg flex-shrink-0">
            {getInitials(lead.name)}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="text-xl font-bold text-gray-900">{lead.name}</h3>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(lead.status)}`}>
                {lead.status}
              </span>
            </div>
            <p className="text-gray-600 mb-2">{lead.title} at {lead.company}</p>
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mb-3">
              <span className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {lead.email}
              </span>
              <span className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                {lead.phone}
              </span>
            </div>
            
            <div className="inline-flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg text-sm">
              <span>{getSourceIcon(lead.source)}</span>
              <span className="font-medium text-blue-600">Source: {lead.source}</span>
            </div>
            {lead.sourceDetail && (
              <p className="text-sm text-gray-500 mt-1">{lead.sourceDetail}</p>
            )}
          </div>
        </div>

        <div className="flex md:flex-col gap-2 items-stretch md:items-end">
          <button className="flex-1 md:flex-initial bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-2">
            <Mail className="w-4 h-4" />
            <span>Contact</span>
          </button>
          <button 
            onClick={onToggle}
            className="flex-1 md:flex-initial border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            <span>View Details</span>
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-700 font-medium mb-1">Deal Value</p>
              <p className="text-2xl font-bold text-green-800">₦{lead.dealValue.toLocaleString()}</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700 font-medium mb-1">Lead Score</p>
              <p className="text-2xl font-bold text-blue-800">{lead.leadScore}/100</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-purple-700 font-medium mb-1">Date Added</p>
              <p className="text-xl font-bold text-purple-800">{lead.dateAdded}</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <p className="text-sm text-orange-700 font-medium mb-1">Last Contact</p>
              <p className="text-xl font-bold text-orange-800">{lead.lastContact}</p>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-gray-700" />
              <h4 className="font-semibold text-gray-900">Recent Actions ({lead.recentActions.length})</h4>
            </div>
            <div className="space-y-2">
              {lead.recentActions.map((action) => (
                <div key={action.id} className="flex items-start gap-3 bg-gray-50 p-3 rounded-lg">
                  <div className="mt-0.5 text-gray-600">
                    {getActionIcon(action.type)}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{action.title}</p>
                    <p className="text-sm text-gray-600">{action.performer} • {action.date}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-blue-700" />
              <h4 className="font-semibold text-blue-900">Next Action</h4>
            </div>
            <p className="text-blue-800">{lead.nextAction.title} - {lead.nextAction.date}</p>
          </div>
        </div>
      )}
    </div>
  );
};